import React from 'react';

// Extracted from StudentProfile.js's renderUpcomingLessons (Phase 3 of the
// platform-health plan: split the file mechanically, without changing
// behavior). Week/lesson data is computed by the parent (via
// computeWeekSchedule) and status-change/edit-lesson/add-lesson handlers
// stay in the parent too, since they touch a lot of parent-only state —
// this component is purely the week grid + mobile day-strip markup.
function formatTime(timeString) {
  if (!timeString) return '';
  // Если время в формате HH:mm:ss, убираем секунды
  if (timeString.includes(':') && timeString.split(':').length === 3) {
    const [hours, minutes] = timeString.split(':');
    return `${hours}:${minutes}`;
  }
  return timeString;
}

const weekdayFormatter = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
const dayNumFormatter = new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'short' });

export default function ScheduleTab({
  weekDays,
  lessonsByDay,
  statusChangeLoading,
  onPrevWeek,
  onNextWeek,
  studentName,
  onLessonStatusChange,
  onEditLesson,
  onAddLesson,
  selectedDayIndex,
  onSelectDay,
}) {
  return (
    <div className="upcoming-lessons-content">
      <div className="week-nav">
        <h3>Class schedule</h3>
        <div className="week-nav-right">
          {statusChangeLoading && <span className="class-schedule-status-spinner" aria-hidden />}
          <div className="week-btns">
          <button type="button" className="week-btn prev" onClick={onPrevWeek} aria-label="Previous week">‹</button>
          <button type="button" className="week-btn next" onClick={onNextWeek} aria-label="Next week">›</button>
        </div>
      </div>
      </div>
      <div className="upcoming-lessons-week">
        <div className="week-grid">
          {weekDays.map((d, idx) => (
            <div key={idx} className="week-column">
              <div className="week-day-label">
                <span className="week-day">{weekdayFormatter.format(d)}</span>
                <span className="week-date">{dayNumFormatter.format(d)}</span>
              </div>
              <div className="week-lessons">
                {lessonsByDay[idx].length === 0 ? (
                  <div className="week-empty">no lessons scheduled</div>
                ) : (
                  lessonsByDay[idx].map(lesson => {
                    // Нормализуем статус для класса CSS
                    const normalizedStatus = (lesson.status || 'in_progress').toLowerCase().replace(/\s+/g, '_');
                    return (
                      <div
                        key={lesson.id}
                        className={`week-lesson-chip status-${normalizedStatus}`}
                        onClick={(e) => {
                          // Не открываем модальное окно при клике на select
                          if (e.target.tagName === 'SELECT') return;
                          onEditLesson(lesson);
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Click to edit lesson"
                      >
                        <div className="week-lesson-meta">
                          <div className="week-lesson-time">{formatTime(lesson.from || lesson.timeFrom || '')}{formatTime(lesson.to || lesson.timeTo) ? `–${formatTime(lesson.to || lesson.timeTo)}` : ''}</div>
                          <div className="week-lesson-title">{studentName || 'Student'}</div>
                          <select
                            value={lesson.status || 'in_progress'}
                            onChange={(e) => {
                              e.stopPropagation(); // Останавливаем всплытие события
                              onLessonStatusChange(lesson.id, e.target.value);
                            }}
                            className="lesson-status-select compact"
                            onClick={(e) => e.stopPropagation()} // Останавливаем всплытие при клике на select
                          >
                            <option value="in_progress">In Progress</option>
                            <option value="done">Done</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                      </div>
                    );
                  })
                )}
                <button
                  className="week-add-lesson-btn"
                  onClick={() => onAddLesson(d)}
                  title="Add lesson"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile-only day-strip + timeline — same visual treatment as
       * the student dashboard's own schedule widget (HomeStudent.js/
       * .css): a horizontal strip of day pills instead of a 7-column
       * grid that's too cramped to read on a phone. CSS toggles which
       * of the two is visible per breakpoint. */}
      <div className="teacher-week-mobile-view">
        <div className="upcoming-lessons-week-strip">
          {weekDays.map((d, idx) => {
            const hasLesson = lessonsByDay[idx].length > 0;
            const isToday = d.toDateString() === new Date().toDateString();
            return (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                className={`upcoming-lessons-day ${hasLesson ? 'has-lesson' : ''} ${selectedDayIndex === idx ? 'selected' : ''} ${isToday ? 'is-today' : ''}`}
                onClick={() => onSelectDay(idx)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectDay(idx); } }}
                aria-pressed={selectedDayIndex === idx}
                aria-label={`${weekdayFormatter.format(d)} ${d.getDate()}${isToday ? ', today' : ''}${hasLesson ? ', has lessons' : ''}`}
              >
                <span className="upcoming-lessons-day-circle" aria-hidden />
                <span className="upcoming-lessons-day-label">{weekdayFormatter.format(d)}</span>
                <span className="upcoming-lessons-day-num">{d.getDate()}</span>
              </div>
            );
          })}
        </div>
        <div className="upcoming-lessons-timeline">
          {selectedDayIndex == null ? (
            <p className="upcoming-lessons-pick-day">Click a day to see lessons</p>
          ) : lessonsByDay[selectedDayIndex].length === 0 ? (
            <p className="upcoming-lessons-no-lessons-day">No lessons on this day</p>
          ) : (
            lessonsByDay[selectedDayIndex].map(lesson => {
              const normalizedStatus = (lesson.status || 'in_progress').toLowerCase().replace(/\s+/g, '_');
              const statusClass = normalizedStatus === 'done' ? 'completed'
                : normalizedStatus === 'in_progress' ? 'in-progress'
                : normalizedStatus === 'cancelled' ? 'cancelled'
                : 'upcoming';
              const statusLabel = statusClass === 'in-progress' ? 'in progress' : statusClass;
              return (
                <div
                  key={lesson.id}
                  className="schedule-lesson-card"
                  onClick={() => onEditLesson(lesson)}
                  title="Click to edit lesson"
                >
                  <div className="schedule-lesson-left">
                    <span className="schedule-lesson-time">{formatTime(lesson.from || lesson.timeFrom || '')}</span>
                    <span className="schedule-lesson-divider" aria-hidden />
                    <span className="schedule-lesson-date">{studentName || 'Student'}</span>
                  </div>
                  <span className={`schedule-lesson-status schedule-lesson-status--${statusClass}`}>
                    <span className={`schedule-lesson-status-icon schedule-lesson-status-icon--${statusClass}`} aria-hidden />
                    {statusLabel}
                  </span>
                </div>
              );
            })
          )}
          {selectedDayIndex != null && (
            <button
              type="button"
              className="teacher-week-mobile-add-btn"
              onClick={() => onAddLesson(weekDays[selectedDayIndex])}
            >
              + Add lesson
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
